<template>
  <div class="page-container">
    <template v-if="true">
      <div class="page-container-body">
        <div class="d-flex align-center ga-2 mb-8">
          <span @click="goBack()" class="goBack">
            <v-icon>
              mds:arrow-back
            </v-icon>
          </span>
          <h3 class="page-title">Manage Payment Method</h3>
        </div>

        <div class="plan-badge mb-4">
          At NuBuilder, we prioritize your security and peace of mind. That’s why all credit card payments on our platform
          are processed securely through <strong>Stripe</strong>, a globally trusted payment provider.
          With <strong>Stripe’s industry-leading encryption and PCI DSS compliance</strong>, your payment details are
          handled with the highest level of security, ensuring they are never stored or exposed on our servers.
          Feel confident knowing your sensitive information is protected every step of the way.
        </div>

        <div class="manage-billing-form">
          <div class="elements" v-show="!isStripeLoading && !!stripe">
            <div id="address-element" class="mb-3"></div>
            <div class="mb-3">
              <label class="Label" for="Field-countryInput">Card number</label>
              <div id="card-number-element" class="Input"></div>
            </div>
            <div class="d-flex justify-space-between ga-3 mb-10">
              <div class="w-50">
                <label class="Label" for="Field-countryInput">Expiration</label>
                <div id="card-expiry-element" class="Input"></div>
              </div>
              <div class="w-50">
                <label class="Label" for="Field-countryInput">CVC</label>
                <div id="card-cvc-element" class="Input"></div>
              </div>
            </div>

            <v-btn
              rounded="lg"
              size="large"
              variant="flat"
              color="#363a40"
              :loading="loading"
              @click="updatePayment"
            >
              Update payment method
            </v-btn>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="page-loading-container-body">
        <v-progress-circular
          indeterminate
          color="white"
          size="32"
          width="2">
        </v-progress-circular>
        <span>Loading subscription...</span>
      </div>
    </template>
    <template v-if="isStripeLoading || !stripe">
      <div class="page-loading-container-body">
        <v-progress-circular
          indeterminate
          color="white"
          size="32"
          width="2">
        </v-progress-circular>
        <span>Loading stripe...</span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'
import store from '@/store'

export default {
  computed: {
    ...mapState({
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    })
  },
  methods: {
    goBack () {
      this.$router.push('/subscription/billing')
    },
    async configureStripe () {
      this.isStripeLoading = true
      try {
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
        this.elements = this.stripe.elements()

        // Address Element
        this.addressElement = this.elements.create('address', {
          mode: 'billing',
          autocomplete: { mode: 'google_maps_api', apiKey: 'AIzaSyDkyS96K5XC-Yt6dHHzU5KilI2xw4Nc5do' },
          defaultValues: {
            name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`
          }
        })
        this.addressElement.mount('#address-element')
        // CardNumber Element
        this.cardNumberElement = this.elements.create('cardNumber')
        this.cardNumberElement.mount('#card-number-element')
        // CardExpiry Element
        this.cardExpiryElement = this.elements.create('cardExpiry')
        this.cardExpiryElement.mount('#card-expiry-element')
        // CardCvc Element
        this.cardCvcElement = this.elements.create('cardCvc')
        this.cardCvcElement.mount('#card-cvc-element')

        this.isStripeLoading = false
      } catch (err) {
        console.log(err)
        this.isStripeLoading = false
        this.$router.push({ path: '/plans' })
      }
    },
    async updatePayment () {
      this.loading = true
      const address = await this.addressElement.getValue()

      const { error: submitError } = await this.elements.submit()
      if (submitError || this.cardNumberElement?._invalid || this.cardExpiryElement?._invalid || this.cardCvcElement?._invalid) {
        let message = ''

        if (submitError) {
          message = submitError.message
        } else if (this.cardNumberElement?._invalid) {
          message = 'Invalid card number'
        } else if (this.cardExpiryElement?._invalid) {
          message = 'Invalid card expiry date'
        } else if (this.cardCvcElement?._invalid) {
          message = 'Invalid card CVC'
        } else {
          message = 'Something went wrong'
        }

        this.loading = false
        store.commit('snackbar/showMessage', {
          content: message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return
      }

      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumberElement,
        billing_details: {
          email: this.currentUser?.email,
          name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`,
          address: {
            city: address.value.address.city,
            country: address.value.address.country,
            line1: address.value.address.line1,
            state: address.value.address.state,
            postal_code: address.value.address.postal_code,
            ...(!!address.value.address.line2 && { line2: address.value.address.line2 })
          }
        }
      })

      if (error) {
        console.error(error)
        this.loading = false
        return store.commit('snackbar/showMessage', {
          content: error.message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }

      const addPaymentMethodParams = { paymentMethodId: paymentMethod.id }

      const addPaymentMethodResponse = await store.dispatch('payments/addPaymentMethod', addPaymentMethodParams)
      if (addPaymentMethodResponse.success) {
        await store.dispatch('payments/getPaymentMethod')
        await store.dispatch('payments/getActiveSubscription')
        this.goBack()
        this.$store.commit('snackbar/showMessage', { content: 'Payment method updated successfully!' }, { root: true })
        this.loading = false
      } else {
        this.loading = false
        return store.commit('snackbar/showMessage', {
          content: addPaymentMethodResponse.message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    }
  },
  data () {
    return {
      loading: false,
      stripe: null,
      elements: null,
      isStripeLoading: false,
      addressElement: null,
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null
    }
  },
  mounted () {
    if (this.currentUser && !this.stripe) {
      this.configureStripe()
    }
  },
  watch: {
    isSubscriptionAdmin: function (val) {
      if (val === false) {
        this.$router.push('/subscription/my-plan')
      }
    },
    currentUser: function (user) {
      if (user) {
        this.configureStripe()
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-container-body {
  flex-grow: 1;
  padding: 0 4px;
  overflow: auto;
}

.page-loading-container-body {
  gap: 16px;
  height: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: var(--dark-4, #363A40);
}

.goBack {
  cursor: pointer;
  color: var(--dark-4, #363A40);
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.billing-info {
  gap: 16px;
  display: flex;
  margin-bottom: 32px;
  flex-direction: column;
}

.info-row {
  gap: 8px;
  display: flex;
  align-items: center;
  color: var(--dark-3, #61676E);
}

.info-row-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;
  color: var(--dark-3, #61676E);
}

.viewBillingHistory {
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  color: var(--blue-4, #0D66D0);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  font-size: 12px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.elements {
  --accessibleColorOnColorPrimary: #fff;
  --p-colorPrimaryHover: #0b81fa;
  --p-colorPrimaryActive: #228cfa;
  --p-colorPrimaryDisabled: #d9d9d9;
  --p-colorPrimaryDisabledText: #8d8d8d;
  --p-colorBackgroundDisabled: #f2f2f2;
  --p-colorBackgroundDisabledDeemphasize05: #e6e6e6;
  --p-colorBackgroundDeemphasize10: #e6e6e6;
  --colorTextPlaceholder: #757680;
  --fontWeightMedium: 600;
  --colorPrimary: #0570de;
  --colorBackground: #fff;
  --colorText: #30313d;
  --borderRadius: 5px;
  --colorPrimaryText: var(--accessibleColorOnColorPrimary);
  --fontSizeSm: 14.88px;
  --p-spacingXs: 1px;
  --p-spacingSm: 2px;
  --p-spacing1: 4px;
  --p-spacing2: 6px;
  --p-spacing3: 12px;
  --fontFamily: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;

  .Input {
    padding: var(--p-spacing3);
    background-color: var(--colorBackground);
    border-radius: var(--borderRadius);
    transition: background 0.15s ease, border 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
    border: 1px solid var(--p-colorBackgroundDeemphasize10);
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
  }

  .Input::placeholder {
    color: var(--colorTextPlaceholder);
  }

  .Input:disabled {
    background-color: var(--p-colorBackgroundDisabled);
    color: var(--colorTextPlaceholder);
    border-color: var(--p-colorBackgroundDisabledDeemphasize05);
    box-shadow: none;
  }

  .Label {
    display: block;
    touch-action: manipulation;
    margin-bottom: var(--p-spacing1);
    font-size: var(--fontSizeSm);
    transition: transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    font-family: var(--fontFamily);
  }

  .Button {
    padding: var(--p-spacing3);
    background-color: var(--colorPrimary);
    font-weight: var(--fontWeightMedium);
    color: var(--colorPrimaryText);
    border-radius: var(--borderRadius);
    transition: background 0.15s ease, color 0.15s ease, border 0.15s ease, box-shadow .15s ease;
  }

  .Button:hover {
    background-color: var(--p-colorPrimaryHover);
  }

  .Button:active {
    background-color: var(--p-colorPrimaryActive);
  }

  .Button:disabled {
    background-color: var(--p-colorPrimaryDisabled);
    color: var(--p-colorPrimaryDisabledText);
  }
}
</style>
